import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { X, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Slide-in card (bottom-right) that shows any active "What's New" posts the
 * current user has not yet dismissed. Posts are stacked inside a single
 * card; clicking "Got it" on a post calls /api/whats-new/{id}/dismiss so it
 * never re-appears for that user.
 *
 * Mounted in Layout so it shows on every authenticated route. Hidden on the
 * login page (token gate).
 */
export const WhatsNewCard = () => {
  const { token, user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [open, setOpen] = useState(false);
  const [dismissing, setDismissing] = useState({});

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const fetchPosts = useCallback(async () => {
    if (!token || !user) return;
    try {
      const r = await axios.get(`${API}/whats-new/me`, { headers, timeout: 10000 });
      const list = r?.data?.posts || [];
      setPosts(list);
      if (list.length > 0) setOpen(true);
    } catch {
      // silent — feature is non-critical
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, user]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const dismiss = async (postId) => {
    setDismissing((d) => ({ ...d, [postId]: true }));
    try {
      await axios.post(`${API}/whats-new/${postId}/dismiss`, {}, { headers });
      setPosts((ps) => ps.filter((p) => p.id !== postId));
    } catch {
      setDismissing((d) => ({ ...d, [postId]: false }));
    }
  };

  if (!token || !user || !open || posts.length === 0) return null;

  return (
    <div
      data-testid="whats-new-card"
      className="fixed bottom-4 right-4 z-50 w-[min(420px,calc(100vw-2rem))] max-h-[80vh] flex flex-col rounded-xl shadow-2xl border border-border bg-background overflow-hidden animate-in slide-in-from-bottom-4 fade-in duration-300"
    >
      <div className="flex items-center justify-between px-4 py-3 bg-primary text-primary-foreground">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4" />
          <span className="font-semibold text-sm">What's New</span>
          {posts.length > 1 && (
            <span className="text-[10px] bg-primary-foreground/20 px-1.5 py-0.5 rounded-full">
              {posts.length}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() => setOpen(false)}
          aria-label="Hide for this session"
          title="Hide for this session (will reappear on next login)"
          data-testid="whats-new-hide-session-btn"
          className="p-1 rounded hover:bg-primary-foreground/15 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="overflow-y-auto divide-y">
        {posts.map((p) => (
          <article key={p.id} className="p-4 space-y-2" data-testid={`whats-new-post-${p.id}`}>
            <h4 className="font-semibold text-base leading-tight" data-testid={`whats-new-title-${p.id}`}>
              {p.title}
            </h4>
            <div
              className="prose prose-sm max-w-none text-foreground/90 [&_h2]:text-base [&_h2]:font-semibold [&_h2]:mt-2 [&_h2]:mb-1 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_a]:text-primary [&_a]:underline"
              dangerouslySetInnerHTML={{ __html: p.body_html }}
            />
            <div className="flex justify-end pt-1">
              <Button
                size="sm"
                onClick={() => dismiss(p.id)}
                disabled={!!dismissing[p.id]}
                data-testid={`whats-new-got-it-${p.id}`}
              >
                {dismissing[p.id] ? "Saving…" : "Got it"}
              </Button>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
};

export default WhatsNewCard;
